import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  SquaresFour, Sparkle, ArrowsClockwise, DownloadSimple, CheckCircle,
  CaretLeft, CaretRight, Plus, Trash, Clock, Eye, Copy, Image, ShareFat
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import { shareOrDownload, downloadFile } from '../utils/share';
import { useUpgrade } from '../components/UpgradeSystem';
import IdeaSuggester from '../components/IdeaSuggester';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: '#e1306c' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0077b5' },
];

const SLIDE_COUNTS = [3, 5, 7, 10];

const SLIDE_TYPES = [
  { id: 'educational_tips', name: 'Educational Tips', desc: 'Share knowledge with numbered tips' },
  { id: 'step_by_step', name: 'Step-by-Step', desc: 'Walk through a process' },
  { id: 'before_after', name: 'Before & After', desc: 'Show transformation' },
  { id: 'listicle', name: 'Listicle / Top N', desc: 'Count down a list' },
  { id: 'testimonial_cards', name: 'Testimonials', desc: 'Showcase customer quotes' },
];

const TONES = ['Authentic', 'Professional', 'Witty', 'Educational', 'Bold'];

const Card = ({ title, children, className = '' }) => (
  <div className={`rounded-xl p-5 ${className}`} style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
    {title && <h3 className="text-sm font-semibold mb-4" style={{ color: '#f5f0e8' }}>{title}</h3>}
    {children}
  </div>
);

/* ═══ Slide Editor ═══ */
const SlideEditor = ({ slide, index, type, onChange, onRemove, canRemove }) => {
  const isTestimonial = type === 'testimonial_cards';
  return (
    <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.25)' }}>
          Slide {index + 1}
        </span>
        {canRemove && (
          <button onClick={onRemove} className="p-1 rounded hover:bg-red-500/10"><Trash size={12} style={{ color: 'rgba(255,68,68,0.4)' }} /></button>
        )}
      </div>
      {isTestimonial ? (
        <>
          <textarea rows={2} value={slide.quote || ''} onChange={e => onChange({ ...slide, quote: e.target.value })}
            placeholder="This product changed everything..." className="w-full px-2.5 py-2 rounded-md text-xs mb-2 resize-none"
            style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
          <div className="grid grid-cols-2 gap-2">
            <input value={slide.author_name || ''} onChange={e => onChange({ ...slide, author_name: e.target.value })}
              placeholder="Name" className="px-2.5 py-2 rounded-md text-xs"
              style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
            <input value={slide.author_title || ''} onChange={e => onChange({ ...slide, author_title: e.target.value })}
              placeholder="Title" className="px-2.5 py-2 rounded-md text-xs"
              style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
          </div>
        </>
      ) : (
        <>
          <input value={slide.title || ''} onChange={e => onChange({ ...slide, title: e.target.value })}
            placeholder={`Point ${index + 1} title`} className="w-full px-2.5 py-2 rounded-md text-xs mb-2"
            style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
          <textarea rows={2} value={slide.body || ''} onChange={e => onChange({ ...slide, body: e.target.value })}
            placeholder="Description..." className="w-full px-2.5 py-2 rounded-md text-xs resize-none"
            style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
        </>
      )}
    </div>
  );
};

export default function CarouselPage() {
  const { handleApiError, checkCreditsBeforeAction } = useUpgrade();
  const [tab, setTab] = useState('create');

  // Form state
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [slideCount, setSlideCount] = useState(5);
  const [slideType, setSlideType] = useState('educational_tips');
  const [tone, setTone] = useState('Professional');
  const [coverFields, setCoverFields] = useState({ headline: '', subheadline: '', brand_name: '' });
  const [slides, setSlides] = useState([{ title: '', body: '' }, { title: '', body: '' }, { title: '', body: '' }]);
  const [ctaFields, setCtaFields] = useState({ cta_headline: '', cta_text: '', brand_name: '' });

  // AI generation
  const [aiGenerating, setAiGenerating] = useState(false);
  const autoWroteRef = useRef(false);

  // Zero Friction: show advanced (per-slide editor, slide count/type, tone) only on demand
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Background image
  const [bgImage, setBgImage] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const bgInputRef = useRef(null);

  const handleBgUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgImage(file);
    setBgPreview(URL.createObjectURL(file));
  };

  const clearBg = () => {
    setBgImage(null);
    setBgPreview(null);
    if (bgInputRef.current) bgInputRef.current.value = '';
  };

  // Render state
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedSlides, setSelectedSlides] = useState(new Set());
  const [editMode, setEditMode] = useState(false);
  const [editBgColor, setEditBgColor] = useState('#080808');

  // Slide selection helpers
  const toggleSlideSelect = (idx) => {
    setSelectedSlides(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  // First Look Celebration handoff — auto-prefill topic + brand on first land
  useEffect(() => {
    try {
      const seedBrand = sessionStorage.getItem('scl_first_look_brand');
      const seedHeadline = sessionStorage.getItem('scl_first_look_headline');
      if (seedBrand) {
        if (!topic) setTopic(seedHeadline || `A 3-slide intro to ${seedBrand}`);
        setCoverFields(prev => ({
          ...prev,
          brand_name: prev.brand_name || seedBrand,
          headline: prev.headline || (seedHeadline || `${seedBrand}.`),
        }));
        setCtaFields(prev => ({ ...prev, brand_name: prev.brand_name || seedBrand }));
        sessionStorage.removeItem('scl_first_look_brand');
        sessionStorage.removeItem('scl_first_look_headline');
        sessionStorage.removeItem('scl_first_look_url');
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectAllSlides = () => {
    if (result) {
      if (selectedSlides.size === result.slides.length) {
        setSelectedSlides(new Set());
      } else {
        setSelectedSlides(new Set(result.slides.map((_, i) => i)));
      }
    }
  };

  // Download selected slides
  const downloadSelected = async () => {
    const indices = selectedSlides.size > 0 ? [...selectedSlides] : result.slides.map((_, i) => i);
    for (const i of indices) {
      const url = `${API}${result.slides[i].download_url}`;
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `carousel-slide-${i + 1}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      await new Promise(r => setTimeout(r, 300));
    }
  };

  // Share selected slides
  const shareSelected = async () => {
    const indices = selectedSlides.size > 0 ? [...selectedSlides] : [currentSlide];
    if (indices.length === 1) {
      const url = result.slides[indices[0]].download_url;
      await shareOrDownload(url, 'image/png', topic || 'Carousel');
    } else {
      // Multi-slide: download all selected
      for (const i of indices) {
        const url = `${API}${result.slides[i].download_url}`;
        const res = await fetch(url);
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `carousel-slide-${i + 1}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        await new Promise(r => setTimeout(r, 300));
      }
      toast.success(`${indices.length} slides downloaded`);
    }
  };
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => { loadHistory(); }, []);

  // Read batch prefill from Marketing Coach
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('batch_prefill');
      if (!raw) return;
      const { type, data } = JSON.parse(raw);
      if (type !== 'carousel' || !data) return;
      sessionStorage.removeItem('batch_prefill');
      if (data.topic) setTopic(data.topic);
      if (data.cover) setCoverFields(prev => ({ ...prev, ...data.cover }));
      if (data.slides?.length) {
        setSlideCount(data.slides.length);
        setSlides(data.slides);
      }
      if (data.cta) setCtaFields(prev => ({ ...prev, ...data.cta }));
      toast.success('Carousel pre-filled from Marketing Coach!');
    } catch {}
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/carousel/history`);
      setHistory(data.carousels || []);
    } catch {}
  };

  // Update slide count
  useEffect(() => {
    const isTest = slideType === 'testimonial_cards';
    const newSlides = Array.from({ length: slideCount }, (_, i) => {
      if (slides[i]) return slides[i];
      return isTest ? { quote: '', author_name: '', author_title: '' } : { title: '', body: '' };
    });
    setSlides(newSlides);
  }, [slideCount, slideType]);

  const updateSlide = (idx, data) => {
    const ns = [...slides];
    ns[idx] = data;
    setSlides(ns);
  };

  const addSlide = () => {
    const isTest = slideType === 'testimonial_cards';
    setSlides([...slides, isTest ? { quote: '', author_name: '', author_title: '' } : { title: '', body: '' }]);
  };

  const removeSlide = (idx) => {
    if (slides.length <= 1) return;
    setSlides(slides.filter((_, i) => i !== idx));
  };

  // AI-generate slide content
  const aiGenerate = async (overrideTopic) => {
    const t = (overrideTopic ?? topic).trim();
    if (!t) { setError('Enter a topic first.'); return; }
    setAiGenerating(true);
    setError('');

    try {
      const { data } = await axios.post(`${API}/api/carousel/ai-generate`, {
        topic: t,
        target_audience: audience || 'general audience',
        num_slides: slideCount + 2,
        goal: slideType,
        tone,
      });
      const gen = data;
      if (gen.cover) setCoverFields(prev => ({ ...prev, ...gen.cover }));
      if (gen.slides?.length) setSlides(gen.slides.slice(0, slideCount));
      if (gen.cta) setCtaFields(prev => ({ ...prev, ...gen.cta }));
    } catch (err) {
      if (!handleApiError(err)) {
        const d = err.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || 'Generation failed.'));
      }
    }
    setAiGenerating(false);
  };

  // Zero Friction: when Brand Profile loads, pre-fill audience / tone / brand name
  // and auto-write all slide content immediately (no blank editors).
  const handleBrandProfileLoad = (p) => {
    if (!p) return;
    if (!audience && p.target_audience) setAudience(p.target_audience);
    if (p.tone_of_voice) {
      const matched = TONES.find((t) => t.toLowerCase() === String(p.tone_of_voice).toLowerCase());
      if (matched) setTone(matched);
    }
    if (p.brand_name) {
      setCoverFields((prev) => ({ ...prev, brand_name: prev.brand_name || p.brand_name }));
      setCtaFields((prev) => ({ ...prev, brand_name: prev.brand_name || p.brand_name }));
    }
    // Auto-write slide content once if user hasn't typed anything yet
    if (autoWroteRef.current) return;
    if (topic.trim()) return; // user already has a topic
    // Once-per-session guard to prevent re-charge on hard reload
    try {
      const sessionKey = 'scl_carousel_autowrote';
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');
    } catch { /* ignore */ }
    const derivedTopic = p.industry
      ? `5 things every ${p.industry} founder needs to know about marketing`
      : (p.brand_name ? `5 reasons ${p.brand_name} is built for people like you` : '');
    if (!derivedTopic) return;
    autoWroteRef.current = true;
    setTopic(derivedTopic);
    // give state a tick, then auto-generate
    setTimeout(() => { aiGenerate(derivedTopic); }, 50);
  };

  // Render carousel
  const renderCarousel = async () => {
    const hasCredits = await checkCreditsBeforeAction('carousel_generate');
    if (!hasCredits) return;

    setRendering(true);
    setResult(null);
    setError('');

    // Fill empty fields with defaults
    const cover = {
      headline: coverFields.headline || topic || 'Your Carousel',
      subheadline: coverFields.subheadline || 'Swipe to learn more',
      brand_name: coverFields.brand_name || 'Brand',
    };
    const cta = {
      cta_headline: ctaFields.cta_headline || 'Ready to get started?',
      cta_text: ctaFields.cta_text || 'Follow for more',
      brand_name: ctaFields.brand_name || cover.brand_name,
    };

    try {
      let bgImageUrl = null;
      if (bgImage) {
        const formData = new FormData();
        formData.append('file', bgImage);
        const uploadRes = await axios.post(`${API}/api/media/upload-bg`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        bgImageUrl = uploadRes.data.url;
      }

      const { data } = await axios.post(`${API}/api/media/carousel/render`, {
        template_id: slideType,
        cover,
        slides,
        cta,
        background_image: bgImageUrl,
      });
      setResult(data);
      setCurrentSlide(0);
      loadHistory();
    } catch (err) {
      if (!handleApiError(err)) {
        const d = err.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || 'Rendering failed.'));
      }
    }
    setRendering(false);
  };

  const downloadAll = () => {
    if (!result?.slides) return;
    result.slides.forEach(s => {
      const a = document.createElement('a');
      a.href = `${API}${s.download_url}`;
      a.download = `carousel-${s.label}.png`;
      a.target = '_blank';
      a.click();
    });
  };

  const typeConfig = SLIDE_TYPES.find(t => t.id === slideType) || SLIDE_TYPES[0];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="carousel-creator-page">
        <EarlyAccessBanner />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Carousel Creator
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
              Build scroll-stopping multi-slide content. Rendered as premium PNGs.
            </p>
          </div>
          <div className="flex gap-2">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'My Carousels'}
              </button>
            ))}
          </div>
        </div>

        {tab === 'create' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* LEFT: Content Brief */}
            <div className="space-y-4">
              <BrandSettingsSummary
                fields={['brand', 'industry', 'tone', 'audience', 'color']}
                onLoad={handleBrandProfileLoad}
              />
              <Card title="Campaign Topic">
                <div className="mb-3 flex justify-end">
                  <IdeaSuggester
                    creator="carousel"
                    compact
                    label="Suggest topics"
                    extraContext={audience}
                    onPick={(idea) => {
                      const title = idea.title || idea.headline || '';
                      if (title) { setTopic(title); aiGenerate(title); }
                    }}
                  />
                </div>
                <div className="mb-3">
                  <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Topic</label>
                  <input data-testid="carousel-topic" value={topic} onChange={e => setTopic(e.target.value)}
                    placeholder="5 reasons solo founders need a marketing system"
                    className="w-full px-3 py-2.5 rounded-lg text-sm"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                </div>
                <button onClick={() => aiGenerate()} disabled={aiGenerating || !topic.trim()} data-testid="ai-generate-carousel-btn"
                  className="w-full py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                  style={{ background: 'rgba(200,240,96,0.08)', border: '1px solid rgba(200,240,96,0.22)', color: '#c8f060', opacity: (!topic.trim() || aiGenerating) ? 0.6 : 1 }}>
                  {aiGenerating ? <><ArrowsClockwise size={14} className="animate-spin" /> Writing your slides…</> : <><Sparkle size={14} weight="fill" /> Rewrite slides from this topic</>}
                </button>
              </Card>

              <button onClick={() => setShowAdvanced((v) => !v)} data-testid="carousel-advanced-toggle"
                className="w-full text-left px-4 py-3 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {showAdvanced ? 'Hide fine-tune options' : 'Fine-tune slides, count & tone'}
                </span>
                <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
              </button>

              {showAdvanced && (
                <>
                  <Card title="Slide Structure">
                <div className="flex gap-2 mb-3">
                  {SLIDE_COUNTS.map(n => (
                    <button key={n} onClick={() => setSlideCount(n)} data-testid={`slide-count-${n}`}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold text-center"
                      style={{
                        background: slideCount === n ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${slideCount === n ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                        color: slideCount === n ? '#c8f060' : 'rgba(245,240,232,0.35)',
                      }}>
                      {n} slides
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SLIDE_TYPES.map(t => (
                    <button key={t.id} onClick={() => setSlideType(t.id)} data-testid={`slide-type-${t.id}`}
                      className="p-2.5 rounded-lg text-left transition-all"
                      style={{
                        background: slideType === t.id ? 'rgba(200,240,96,0.06)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${slideType === t.id ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                      }}>
                      <p className="text-[11px] font-semibold" style={{ color: slideType === t.id ? '#c8f060' : '#f5f0e8' }}>{t.name}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'rgba(245,240,232,0.2)' }}>{t.desc}</p>
                    </button>
                  ))}
                </div>
              </Card>

              <Card title="Content Settings">
                <div className="flex flex-wrap gap-2 mb-3">
                  {TONES.map(t => (
                    <button key={t} onClick={() => setTone(t)}
                      className="px-3 py-1.5 rounded-full text-[11px] font-medium"
                      style={{
                        background: tone === t ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${tone === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                        color: tone === t ? '#c8f060' : 'rgba(245,240,232,0.3)',
                      }}>
                      {t}
                    </button>
                  ))}
                </div>
              </Card>

              <Card title="Cover Slide">
                <input value={coverFields.headline} onChange={e => setCoverFields(f => ({ ...f, headline: e.target.value }))}
                  placeholder="Your carousel headline" className="w-full px-2.5 py-2 rounded-md text-xs mb-2"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
                <input value={coverFields.subheadline} onChange={e => setCoverFields(f => ({ ...f, subheadline: e.target.value }))}
                  placeholder="Swipe to learn more" className="w-full px-2.5 py-2 rounded-md text-xs mb-2"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
                <input value={coverFields.brand_name} onChange={e => setCoverFields(f => ({ ...f, brand_name: e.target.value }))}
                  placeholder="Brand name" className="w-full px-2.5 py-2 rounded-md text-xs"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
              </Card>

              <Card title={`Content Slides (${slides.length})`}>
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {slides.map((slide, i) => (
                    <SlideEditor key={i} slide={slide} index={i} type={slideType}
                      onChange={d => updateSlide(i, d)} onRemove={() => removeSlide(i)} canRemove={slides.length > 1} />
                  ))}
                </div>
                <button onClick={addSlide} className="w-full mt-2 py-2 rounded-lg text-[11px] font-medium flex items-center justify-center gap-1"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.3)' }}>
                  <Plus size={12} /> Add Slide
                </button>
              </Card>

              <Card title="CTA Slide">
                <input value={ctaFields.cta_headline} onChange={e => setCtaFields(f => ({ ...f, cta_headline: e.target.value }))}
                  placeholder="Ready to get started?" className="w-full px-2.5 py-2 rounded-md text-xs mb-2"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
                <input value={ctaFields.cta_text} onChange={e => setCtaFields(f => ({ ...f, cta_text: e.target.value }))}
                  placeholder="Follow for more" className="w-full px-2.5 py-2 rounded-md text-xs"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
              </Card>
                </>
              )}

              {/* Background Image Upload */}
              <Card title="Background Image (optional)">
                <input ref={bgInputRef} type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
                {bgPreview ? (
                  <div className="relative">
                    <img src={bgPreview} alt="Background" className="w-full h-24 object-cover rounded-lg mb-2" style={{ opacity: 0.7 }} />
                    <button onClick={clearBg} className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px]"
                      style={{ background: 'rgba(0,0,0,0.7)', color: '#ff6b6b' }}>x</button>
                    <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>Background will be applied to all slides</p>
                  </div>
                ) : (
                  <button onClick={() => bgInputRef.current?.click()} data-testid="upload-bg-btn"
                    className="w-full py-6 rounded-lg text-xs font-medium flex flex-col items-center gap-2 transition-all hover:border-opacity-30"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.3)' }}>
                    <Image size={20} />
                    <span>Upload a background image for all slides</span>
                    <span className="text-[9px]" style={{ color: 'rgba(245,240,232,0.15)' }}>PNG, JPG up to 5MB</span>
                  </button>
                )}
              </Card>

              {/* Error */}
              {error && (
                <div className="p-3 rounded-lg" style={{ background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.12)' }}>
                  <p className="text-xs" style={{ color: '#ff6b6b' }}>{typeof error === 'string' ? error : 'An error occurred'}</p>
                </div>
              )}

              {/* Render Button */}
              <button onClick={renderCarousel} disabled={rendering} data-testid="render-carousel-btn"
                className="w-full py-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                style={{
                  background: rendering ? 'rgba(200,240,96,0.06)' : '#c8f060',
                  color: rendering ? '#c8f060' : '#080808',
                  opacity: rendering ? 0.7 : 1,
                }}>
                {rendering ? <><ArrowsClockwise size={16} className="animate-spin" /> Rendering slides...</> : <><Image size={16} /> Render Carousel (3 credits)</>}
              </button>
            </div>

            {/* RIGHT: Preview Panel */}
            <div className="space-y-4">
              <Card>
                <h3 className="text-sm font-semibold mb-4" style={{ color: '#f5f0e8' }}>Preview</h3>

                {/* Phone Frame */}
                <div className="mx-auto" style={{ maxWidth: '360px' }}>
                  <div className="rounded-2xl overflow-hidden relative" style={{
                    aspectRatio: '1/1',
                    background: '#0a0a0a',
                    border: '2px solid rgba(255,255,255,0.06)',
                  }}>
                    {rendering && (
                      <div className="flex flex-col items-center justify-center h-full">
                        <ArrowsClockwise size={24} className="animate-spin mb-3" style={{ color: '#c8f060' }} />
                        <p className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>Rendering {slides.length + 2} slides...</p>
                      </div>
                    )}

                    {result && !rendering && (
                      <>
                        <img
                          data-testid="carousel-preview-image"
                          src={`${API}${result.slides[currentSlide]?.download_url}`}
                          alt={`Slide ${currentSlide + 1}`}
                          className="w-full h-full object-contain"
                        />
                        {/* Edit overlay */}
                        {editMode && (
                          <div className="absolute inset-0 flex flex-col justify-end p-3"
                            style={{ background: 'rgba(0,0,0,0.5)' }}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.5)' }}>Edit Slide {currentSlide + 1}</span>
                            </div>
                            <textarea
                              data-testid="slide-edit-text"
                              value={slides[currentSlide - 1]?.title || coverFields.headline || ctaFields.cta_headline || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (currentSlide === 0) setCoverFields(p => ({ ...p, headline: val }));
                                else if (currentSlide === (result?.slides?.length || 1) - 1) setCtaFields(p => ({ ...p, cta_headline: val }));
                                else {
                                  const idx = currentSlide - 1;
                                  setSlides(prev => prev.map((s, i) => i === idx ? { ...s, title: val } : s));
                                }
                              }}
                              className="w-full rounded-lg px-2.5 py-2 text-xs resize-none"
                              rows={2}
                              style={{ background: 'rgba(255,255,255,0.1)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.15)' }}
                              placeholder="Edit slide text..."
                            />
                            <p className="text-[9px] mt-1" style={{ color: 'rgba(200,240,96,0.5)' }}>Edit text above, then click Re-render to apply changes</p>
                          </div>
                        )}
                      </>
                    )}

                    {!rendering && !result && (
                      <div className="flex flex-col items-center justify-center h-full text-center p-6">
                        <SquaresFour size={32} style={{ color: 'rgba(200,240,96,0.15)' }} className="mb-3" />
                        <p className="text-xs font-medium mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Your carousel will appear here</p>
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.15)' }}>Fill in slides and hit Render</p>
                      </div>
                    )}
                  </div>

                  {/* Navigation + Edit toggle */}
                  {result && !rendering && (
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3">
                        <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0}
                          className="p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', opacity: currentSlide === 0 ? 0.3 : 1 }}>
                          <CaretLeft size={14} style={{ color: '#f5f0e8' }} />
                        </button>
                        <div className="flex gap-1.5">
                          {result.slides.map((_, i) => (
                            <button key={i} onClick={() => setCurrentSlide(i)}
                              className="w-2 h-2 rounded-full transition-all"
                              style={{ background: i === currentSlide ? '#c8f060' : 'rgba(245,240,232,0.1)' }} />
                          ))}
                        </div>
                        <button onClick={() => setCurrentSlide(Math.min(result.slides.length - 1, currentSlide + 1))}
                          disabled={currentSlide >= result.slides.length - 1}
                          className="p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', opacity: currentSlide >= result.slides.length - 1 ? 0.3 : 1 }}>
                          <CaretRight size={14} style={{ color: '#f5f0e8' }} />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setEditMode(!editMode)} data-testid="edit-slide-btn"
                          className="px-2.5 py-1 rounded text-[10px] font-medium"
                          style={{
                            background: editMode ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.03)',
                            color: editMode ? '#c8f060' : 'rgba(245,240,232,0.35)',
                            border: `1px solid ${editMode ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                          }}>
                          Edit
                        </button>
                        {editMode && (
                          <button onClick={() => { setEditMode(false); renderCarousel(); }} data-testid="re-render-btn"
                            className="px-2.5 py-1 rounded text-[10px] font-medium"
                            style={{ background: '#c8f060', color: '#080808' }}>
                            Re-render
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Slide thumbnails with selection */}
                {result && !rendering && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={selectAllSlides} data-testid="select-all-slides-btn"
                        className="text-[10px] font-medium px-2.5 py-1 rounded"
                        style={{
                          background: selectedSlides.size === result.slides.length ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.03)',
                          color: selectedSlides.size === result.slides.length ? '#c8f060' : 'rgba(245,240,232,0.35)',
                          border: `1px solid ${selectedSlides.size === result.slides.length ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                        }}>
                        {selectedSlides.size === result.slides.length ? 'Deselect All' : 'Select All'}
                      </button>
                      {selectedSlides.size > 0 && (
                        <span className="text-[10px]" style={{ color: 'rgba(200,240,96,0.6)' }}>
                          {selectedSlides.size} selected
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {result.slides.map((s, i) => (
                        <button key={i} onClick={() => { setCurrentSlide(i); toggleSlideSelect(i); }}
                          className="shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all relative"
                          style={{ border: `2px solid ${selectedSlides.has(i) ? '#c8f060' : i === currentSlide ? 'rgba(200,240,96,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
                          <img src={`${API}${s.download_url}`} alt="" className="w-full h-full object-cover" />
                          {selectedSlides.has(i) && (
                            <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#c8f060' }}>
                              <CheckCircle size={10} weight="fill" style={{ color: '#080808' }} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {result && !rendering && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={downloadSelected} data-testid="download-carousel-btn"
                      className="flex-1 py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                      style={{ background: '#c8f060', color: '#080808' }}>
                      <DownloadSimple size={14} /> {selectedSlides.size > 0 ? `Download (${selectedSlides.size})` : 'Download All'}
                    </button>
                    <button onClick={shareSelected} data-testid="share-carousel-btn"
                      className="py-2.5 px-4 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5"
                      style={{ background: 'rgba(168,139,250,0.08)', border: '1px solid rgba(168,139,250,0.2)', color: '#a78bfa' }}>
                      <ShareFat size={14} /> Share
                    </button>
                    <button onClick={() => { setResult(null); setCurrentSlide(0); setSelectedSlides(new Set()); }}
                      className="px-4 py-2.5 rounded-lg text-xs font-medium"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)' }}>
                      New
                    </button>
                  </div>
                )}

                {result && (
                  <p className="text-[10px] text-center mt-2" style={{ color: 'rgba(245,240,232,0.15)' }}>
                    {result.total_slides} slides &middot; {result.width}x{result.height}px &middot; {result.credits_used} credits
                  </p>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div>
            {history.length === 0 ? (
              <div className="text-center py-20">
                <SquaresFour size={40} style={{ color: 'rgba(245,240,232,0.1)' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No carousels yet. Create your first one above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {history.map(c => (
                  <div key={c.carousel_id} className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="aspect-square bg-black/30">
                      {c.slides?.[0]?.download_url && (
                        <img src={`${API}${c.slides[0].download_url}`} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-medium" style={{ color: '#f5f0e8' }}>{c.cover?.headline || c.template_id}</p>
                      <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>
                        {c.total_slides} slides &middot; {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
